<script setup lang="ts">
import type { FilterOption, FilterSection } from '@/types/filter.types'
import { computed, onMounted, onUnmounted, ref } from 'vue'

const props = defineProps<{
  filterSections: FilterSection[]
}>()

const emit = defineEmits<{
  save: [selectedOptions: FilterOption[]]
}>()

const isOpen = ref(false)
const selectedOptions = ref<FilterOption[]>(
  props.filterSections.map((section) => section.selectedOption),
)

const openBottomSheet = () => {
  selectedOptions.value = props.filterSections.map((section) => section.selectedOption)
  isOpen.value = true
  document.body.style.overflow = 'hidden'
}

const closeBottomSheet = () => {
  isOpen.value = false
  document.body.style.overflow = ''
}

const handleSelectOption = (sectionIndex: number, option: FilterOption) => {
  selectedOptions.value[sectionIndex] = option
}

const handleSave = () => {
  emit('save', selectedOptions.value)
  closeBottomSheet()
}

const canSave = computed(() => {
  return selectedOptions.value.some(
    (option, index) => option !== props.filterSections[index].selectedOption,
  )
})

onMounted(() => {
  selectedOptions.value = props.filterSections.map((section) => section.selectedOption)
})

onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<template>
  <div class="bottom-sheet-filter">
    <img
      class="cursor-pointer svg-24 svg-white"
      src="@/assets/svgs/filter.svg"
      alt="filter"
      @click="openBottomSheet"
    />

    <Teleport to="body">
      <div v-if="isOpen" class="bottom-sheet-overlay" @click="closeBottomSheet"></div>
      <div v-if="isOpen" class="bottom-sheet">
        <div class="bottom-sheet-content">
          <div
            v-for="(section, sectionIndex) in filterSections"
            :key="section.title"
            class="filter-section"
          >
            <div class="filter-title body-md white">{{ section.title }}</div>
            <div class="filter-options">
              <button
                v-for="option in section.options"
                :key="option.data"
                class="option-button body-sm"
                :class="{ selected: selectedOptions[sectionIndex].data === option.data }"
                @click="handleSelectOption(sectionIndex, option)"
              >
                {{ option.name }}
              </button>
            </div>
          </div>
        </div>
        <button
          class="save-button body-md gray-600"
          :class="{ 'can-save': canSave }"
          @click="handleSave"
          :disabled="!canSave"
        >
          저장하기
        </button>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.bottom-sheet-filter {
  display: flex;
  align-items: center;
  justify-content: center;
}

.bottom-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(63, 65, 71, 0.4);
  z-index: 999;
}

.bottom-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: var(--gray-900);
  border-radius: 8px 8px 0 0;
  z-index: 1000;
  padding: 32px 20px;
  animation: slide-up 0.3s ease;
  display: flex;
  flex-direction: column;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
}

.bottom-sheet-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-height: 50vh;
  overflow-y: auto;
  padding-bottom: 0px;
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-button {
  padding: 6px 16px;
  border-radius: 8px;
  background-color: var(--gray-800);
  border: none;
  color: var(--gray-600);
  cursor: pointer;
}

.option-button.selected {
  background-color: var(--tint-500);
  border: none;
  color: white;
}

.save-button {
  width: 100%;
  padding: 16px;
  background-color: var(--gray-800);
  color: var(--gray-600);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 32px;
}

.save-button.can-save {
  background-color: var(--tint-500);
  color: var(--white);
}

@keyframes slide-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>
